import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../utils/auth.js';
import { autoLoginKpi } from '../services/kpiIntegrationApi.js';
import { autoLoginNovaMatch } from '../services/novaMatchIntegrationApi.js';
import { getNovaErpStatus, connectNovaErp, disconnectNovaErp, autoLoginNovaErp } from '../services/novaErpIntegrationApi.js';
import { loadRecentApps, rememberRecentApp } from '../utils/recentApps.js';
import {
  FiArrowLeft,
  FiArrowUpRight,
  FiBarChart2,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiGrid,
  FiLayers,
  FiMessageSquare,
  FiRepeat,
  FiShield,
  FiTool,
  FiUserCheck
} from 'react-icons/fi';
import ThemeToggle from './ThemeToggle.jsx';
import TourButton from './tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from './help/HelpMenu.jsx';
import TourMode from './tour/TourMode.jsx';
import { usePageTour } from './tour/usePageTour.js';
import '../styles/my-apps.css';

function readEmailFromToken() {
  try {
    const token = authService.getToken();
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email || '';
  } catch {
    return '';
  }
}

const WORK_APPS = [
  {
    id: 'novabotx',
    name: 'NovaBotX',
    description: 'AI chat assistant for HR and workplace questions',
    accent: 'var(--nb-blue)',
    tag: 'AI assistant',
    icon: <FiMessageSquare aria-hidden="true" />
  },
  {
    id: 'novamatch',
    name: 'NovaMatch',
    description: 'AI resume analysis and job-match reports',
    accent: 'var(--nb-teal)',
    tag: 'Talent',
    icon: <FiUserCheck aria-hidden="true" />
  },
  {
    id: 'novaerp',
    name: 'NovaERP',
    description: 'Novalink\u2019s ERP for day-to-day business operations',
    accent: 'var(--nb-green)',
    tag: 'ERP',
    icon: <FiBriefcase aria-hidden="true" />
  },
  {
    id: 'novakpi',
    name: 'NovaKPI',
    description: 'KPI dashboards and performance insights',
    accent: 'var(--nb-warning)',
    tag: 'Analytics',
    icon: <FiBarChart2 aria-hidden="true" />
  }
];

const OFFICE_TOOLS = [
  {
    id: 'sign-pdf',
    name: 'Sign PDF',
    description: 'Draw, type, or upload a visual signature stamp',
    accent: 'var(--nb-blue-600)',
    tag: 'In-browser',
    icon: <FiEdit3 aria-hidden="true" />
  },
  {
    id: 'pdf-tools',
    name: 'PDF Tools',
    description: 'Merge, split, or lightly compress PDF files',
    accent: 'var(--nb-danger)',
    tag: 'PDF',
    icon: <FiLayers aria-hidden="true" />
  },
  {
    id: 'file-convert',
    name: 'File Convert',
    description: 'Convert any supported file to PDF, Word, images, and more',
    accent: 'var(--nb-teal)',
    tag: 'Convert',
    icon: <FiRepeat aria-hidden="true" />
  }
];

const ALL_APPS = [...WORK_APPS, ...OFFICE_TOOLS];

function AppCard({ app, openingId, onOpen }) {
  const isOpening = openingId === app.id;
  return (
    <button
      type="button"
      className={`my-apps-card ${isOpening ? 'is-opening' : ''}`}
      onClick={() => onOpen(app.id)}
      disabled={Boolean(openingId)}
      style={{ '--app-accent': app.accent }}
      aria-busy={isOpening || undefined}
    >
      <span className="my-apps-card-top">
        <span className="my-apps-card-icon">{app.icon}</span>
        <span className="my-apps-card-go" aria-hidden="true">
          {isOpening ? <span className="my-apps-spinner" /> : <FiArrowUpRight />}
        </span>
      </span>
      <span className="my-apps-card-name">
        {isOpening ? 'Opening…' : app.name}
        {app.comingSoon && <span className="my-apps-card-soon">Coming soon</span>}
      </span>
      <span className="my-apps-card-desc">{app.description}</span>
      {app.tag && <span className="my-apps-card-tag">{app.tag}</span>}
    </button>
  );
}

const APPS_TOUR_STEPS = [
  {
    target: 'apps-nav',
    title: 'Your launcher',
    body: 'This sidebar is home base. Jump back to NovaBotX chat any time, or open the admin console if you have access.'
  },
  {
    target: 'apps-work',
    title: 'Work apps',
    body: 'NovaMatch and NovaKPI open with single sign-on — one click and you are in, no second login. Connect NovaERP once to ask NovaBotX about your leave and colleagues.'
  },
  {
    target: 'apps-tools',
    title: 'Office tools',
    body: 'Sign, merge, split, compress and convert documents right here. Files are processed on the fly and never stored.'
  },
  {
    target: 'apps-actions',
    title: 'Make it yours',
    body: 'Switch between light and dark mode, or replay this tour any time. Apps you open often show up in a “Frequently used” row at the top.'
  }
];

export default function MyAppsPage() {
  const navigate = useNavigate();
  const [recentIds, setRecentIds] = useState(() => loadRecentApps());
  const [openingId, setOpeningId] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState(() => readEmailFromToken());
  const { touring, start: startTour, end: endTour } = usePageTour();
  const [searchParams, setSearchParams] = useSearchParams();
  const [erp, setErp] = useState({ configured: false, connected: false });
  const [erpBusy, setErpBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    getNovaErpStatus()
      .then((data) => { if (!cancelled) setErp({ configured: Boolean(data.configured), connected: Boolean(data.connected) }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // NovaERP sends people back here with ?novaerp=connected|denied|expired|error
  useEffect(() => {
    const result = searchParams.get('novaerp');
    if (!result) return;
    const messages = {
      connected: 'NovaERP is connected. Ask NovaBotX things like “how much leave do I have left?” or “who is off this week?”.',
      denied: 'NovaERP was not connected because access was not allowed.',
      expired: 'That NovaERP sign-in took too long. Please connect again.',
      error: 'NovaERP could not be connected. Please try again.'
    };
    if (result === 'connected') setErp((prev) => ({ ...prev, connected: true }));
    if (result === 'connected' || result === 'denied') setNotice(messages[result]);
    else setError(messages[result] || messages.error);
    const next = new URLSearchParams(searchParams);
    next.delete('novaerp');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleErpConnect = async () => {
    setError('');
    setErpBusy(true);
    try {
      await connectNovaErp();
    } catch (err) {
      if (authService.isSessionExpiredError(err)) { authService.redirectToLogin(); return; }
      setError(err?.message || 'Could not connect NovaERP');
      setErpBusy(false);
    }
  };

  const handleErpDisconnect = async () => {
    setError('');
    setErpBusy(true);
    try {
      await disconnectNovaErp();
      setErp((prev) => ({ ...prev, connected: false }));
      setNotice('NovaERP is disconnected. NovaBotX no longer reads your NovaERP data.');
    } catch (err) {
      setError(err?.message || 'Could not disconnect NovaERP');
    } finally {
      setErpBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    authService.getProfile()
      .then((profile) => {
        const user = profile?.data?.user || profile?.user;
        if (!cancelled && user?.email) setEmail(user.email);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const appsById = useMemo(() => Object.fromEntries(ALL_APPS.map((app) => [app.id, app])), []);
  const frequentApps = recentIds.map((id) => appsById[id]).filter(Boolean);
  const showFrequent = frequentApps.length > 0;

  const markOpened = (appId) => {
    setRecentIds(rememberRecentApp(appId));
  };

  const openApp = async (appId) => {
    if (openingId) return;
    setError('');
    setOpeningId(appId);

    try {
      if (appId === 'novabotx') {
        markOpened(appId);
        navigate('/chat');
        return;
      }

      if (appId === 'sign-pdf') {
        markOpened(appId);
        navigate('/tools/sign-pdf');
        return;
      }

      if (appId === 'pdf-tools') {
        markOpened(appId);
        navigate('/tools/pdf');
        return;
      }

      if (appId === 'file-convert') {
        markOpened(appId);
        navigate('/tools/convert');
        return;
      }

      if (appId === 'novakpi') {
        const data = await autoLoginKpi();
        if (!data?.redirectUrl) throw new Error('NovaKPI did not return a redirect URL');
        markOpened(appId);
        window.location.href = data.redirectUrl;
        return;
      }

      if (appId === 'novaerp') {
        // Single sign-on when NovaERP has it set up; otherwise NovaERP's own sign-in page.
        const data = await autoLoginNovaErp();
        if (!data?.redirectUrl) throw new Error('NovaERP did not return a redirect URL');
        markOpened(appId);
        window.location.href = data.redirectUrl;
        return;
      }

      if (appId === 'novamatch') {
        const data = await autoLoginNovaMatch();
        if (!data?.redirectUrl) throw new Error('NovaMatch did not return a redirect URL');
        markOpened(appId);
        window.location.href = data.redirectUrl;
        return;
      }

      throw new Error('Unknown app');
    } catch (err) {
      console.error(`[MyApps] Failed to open ${appId}:`, err);
      if (authService.isSessionExpiredError(err)) {
        authService.redirectToLogin();
        return;
      }
      setError(err?.message || `Could not open ${appsById[appId]?.name || 'app'}`);
      setOpeningId('');
    }
  };

  const initial = (email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="my-apps-page">
      <div className="my-apps-shell">
        <aside className="my-apps-sidebar">
          <div className="my-apps-sidebar-top">
            <div className="my-apps-brand">
              <span className="my-apps-brand-mark" aria-hidden="true"><FiGrid /></span>
              <div>
                <h1 className="my-apps-sidebar-title">My Apps</h1>
                <span className="my-apps-brand-sub">NovaLink workspace</span>
              </div>
            </div>
            <button type="button" className="my-apps-back-link" onClick={() => navigate('/chat')}>
              <FiArrowLeft aria-hidden="true" /> Back to chat
            </button>
          </div>
          <nav className="my-apps-sidebar-nav" aria-label="Apps navigation" data-tour="apps-nav">
            <button type="button" className="my-apps-nav-item is-active" aria-current="page">
              <FiGrid aria-hidden="true" />
              My Apps
            </button>
            {authService.isAdmin() && (
              <button type="button" className="my-apps-nav-item" onClick={() => navigate('/admin')}>
                <FiShield aria-hidden="true" />
                Admin
              </button>
            )}
          </nav>
          {email && (
            <div className="my-apps-sidebar-user">
              <span className="my-apps-avatar" aria-hidden="true">{initial}</span>
              <span className="my-apps-user-text">
                Signed in as
                <strong>{email}</strong>
              </span>
            </div>
          )}
        </aside>

        <main className="my-apps-main">
          <header className="my-apps-header">
            <div className="my-apps-header-text">
              <span className="my-apps-kicker">Workspace</span>
              <h2 className="my-apps-title">My Apps</h2>
              <p className="my-apps-subtitle">
                Launch NovaLink apps and office tools with the same account.
              </p>
            </div>
            <div className="my-apps-header-actions" data-tour="apps-actions">
              <HelpMenu onTour={startTour} />
              <FeedbackButton />
              <TourButton onClick={startTour} />
              <ThemeToggle />
            </div>
          </header>

          {error && (
            <div className="my-apps-error" role="alert">
              {error}
            </div>
          )}

          {notice && (
            <div className="my-apps-notice" role="status">
              {notice}
            </div>
          )}

          {showFrequent && (
            <section className="my-apps-section">
              <div className="my-apps-section-header">
                <h3><FiClock aria-hidden="true" /> Frequently used</h3>
                <span className="my-apps-count">{frequentApps.length}</span>
              </div>
              <div className="my-apps-grid nb-stagger">
                {frequentApps.map((app) => (
                  <AppCard key={`freq-${app.id}`} app={app} openingId={openingId} onOpen={openApp} />
                ))}
              </div>
            </section>
          )}

          <section className="my-apps-section" data-tour="apps-work">
            <div className="my-apps-section-header">
              <h3><FiBriefcase aria-hidden="true" /> Work apps</h3>
              <span className="my-apps-count">{WORK_APPS.length}</span>
            </div>
            <div className="my-apps-grid nb-stagger">
              {WORK_APPS.map((app) => (
                <AppCard key={app.id} app={app} openingId={openingId} onOpen={openApp} />
              ))}
            </div>
            {erp.configured && (
              <div className="my-apps-connect" data-tour="apps-novaerp">
                <div className="my-apps-connect-text">
                  <strong>NovaERP in chat</strong>
                  <span>
                    {erp.connected
                      ? 'NovaBotX can read your NovaERP data (read-only) to answer questions about leave and colleagues.'
                      : 'Let NovaBotX read your NovaERP data (read-only) so you can ask about leave and colleagues. You only see what NovaERP lets you see.'}
                  </span>
                </div>
                {erp.connected && (
                  <span className="my-apps-connect-state"><FiCheckCircle aria-hidden="true" /> Connected</span>
                )}
                <div className="my-apps-connect-actions">
                  {erp.connected ? (
                    <>
                      <button type="button" className="my-apps-connect-btn is-primary" onClick={() => navigate('/chat')}>Ask NovaBotX</button>
                      <button type="button" className="my-apps-connect-btn" onClick={handleErpDisconnect} disabled={erpBusy}>Disconnect</button>
                    </>
                  ) : (
                    <button type="button" className="my-apps-connect-btn is-primary" onClick={handleErpConnect} disabled={erpBusy}>
                      {erpBusy ? 'Opening NovaERP…' : 'Connect NovaERP'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="my-apps-section" data-tour="apps-tools">
            <div className="my-apps-section-header">
              <h3><FiTool aria-hidden="true" /> Office tools</h3>
              <span className="my-apps-count">{OFFICE_TOOLS.length}</span>
            </div>
            <div className="my-apps-grid nb-stagger">
              {OFFICE_TOOLS.map((app) => (
                <AppCard key={app.id} app={app} openingId={openingId} onOpen={openApp} />
              ))}
            </div>
          </section>
        </main>
      </div>
      {touring && <TourMode steps={APPS_TOUR_STEPS} topOffset={8} onClose={endTour} />}
    </div>
  );
}
